import { useCallback, useEffect, useRef } from 'react';
import { useSelector } from '@xstate/store/react';
import { mainNavStore, NAV_STAGE_WIDTHS, type NavStage } from '../stores/mainNavStore';
import { setDragBodyStyles, stopDragBodyStyles } from '../lib/dragResizeUtils';

export function useMainNavResize() {
  const navStage = useSelector(mainNavStore, (s) => s.context.stage);
  const rawWidth = useSelector(mainNavStore, (s) => s.context.rawWidth);
  const isDragging = useSelector(mainNavStore, (s) => s.context.isDragging);

  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  // Track whether THIS hook initiated the drag (vs sidebar oversteer)
  const ownDragRef = useRef(false);

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    ownDragRef.current = true;
    startXRef.current = e.clientX;
    startWidthRef.current = mainNavStore.getSnapshot().context.rawWidth;
    mainNavStore.send({ type: 'nav.startDrag' });
  }, []);

  const handleDoubleClick = useCallback(() => {
    const current = mainNavStore.getSnapshot().context.stage;
    const nextStage: NavStage = current === 'tiny' ? 'expanded' : 'tiny';
    mainNavStore.send({ type: 'nav.setStage', stage: nextStage });
  }, []);

  useEffect(() => {
    // Only set up handlers if THIS hook started the drag
    if (!isDragging || !ownDragRef.current) return;

    setDragBodyStyles('col-resize');

    const handleMouseMove = (e: MouseEvent) => {
      const delta = e.clientX - startXRef.current;
      const newRawWidth = Math.max(
        NAV_STAGE_WIDTHS['tiny'],
        Math.min(NAV_STAGE_WIDTHS['expanded'], startWidthRef.current + delta)
      );
      mainNavStore.send({ type: 'nav.setRawWidth', width: newRawWidth });
    };

    const handleMouseUp = () => {
      ownDragRef.current = false;
      mainNavStore.send({ type: 'nav.endDrag' });
      stopDragBodyStyles();
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      stopDragBodyStyles();
    };
  }, [isDragging]);

  return {
    navWidth: rawWidth,
    navStage,
    isResizing: isDragging,
    handleResizeStart,
    handleDoubleClick,
  };
}
